import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ValetApi from "../api/Api";
import { formatPhone } from "../utility/formatPhone";
import { VALET_LIABILITY_TERMS } from "../constants/liabilityTerms.js";
import CustomerPaymentPanel from "./CustomerPaymentPanel";
// pickup modal dependencies
import { QRCodeSVG } from "qrcode.react";
import "./CustomerVehicleInfo.css";

const CUSTOMER_LANDING_REFRESH_MS = 10000;

const CustomerVehicleInfo = () => {
    const { ticketNum, customerToken } = useParams();
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const [showTerms, setShowTerms] = useState(false);
    const [surveyPath, setSurveyPath] = useState("");
    const [paymentStatus, setPaymentStatus] = useState(null);
    const surveyAlreadySubmitted = Boolean(vehicle?.hasSubmittedSurveyThisVisit);

    useEffect(() => {
        let isMounted = true;
        let isFetchInFlight = false;

        async function fetchVehicle({ showLoading = false, surfaceError = false } = {}) {
            if (isFetchInFlight) return;

            isFetchInFlight = true;
            try {
                if (showLoading && isMounted) {
                    setLoading(true);
                }
                if (surfaceError && isMounted) {
                    setError(null);
                }

                const res = await ValetApi.getCustomerVehicle(ticketNum, customerToken);
                if (isMounted) {
                    setVehicle(res.data.customer);
                    setError(null);
                }
            } catch (err) {
                console.error(err);
                if (isMounted && surfaceError) {
                    const msg = Array.isArray(err) ? err[0] : "Could not load vehicle information.";
                    setError(msg);
                }
            } finally {
                if (showLoading && isMounted) {
                    setLoading(false);
                }
                isFetchInFlight = false;
            }
        }

        fetchVehicle({ showLoading: true, surfaceError: true });

        const refreshIntervalId = window.setInterval(() => {
            fetchVehicle();
        }, CUSTOMER_LANDING_REFRESH_MS);

        return () => {
            isMounted = false;
            window.clearInterval(refreshIntervalId);
        };
    }, [ticketNum, customerToken]);

    const canOpenSurvey = Number(vehicle?.statusId) === 6;

    useEffect(() => {
        async function fetchSurveyInvite() {
            if (!vehicle || !canOpenSurvey || surveyPath || surveyAlreadySubmitted) return;
            try {
                const res = await ValetApi.createCustomerSurveyInvite(ticketNum, customerToken);
                const token = res.data.invite.token;
                setSurveyPath(`/survey/customer/${token}`);
            } catch (err) {
                // leave survey button disabled if invite cannot be created
            }
        }
        fetchSurveyInvite();
    }, [vehicle, canOpenSurvey, surveyPath, ticketNum, customerToken, surveyAlreadySubmitted]);

    const handleRequestVehicle = async () => {
        if (!vehicle) return;

        const canRequestVehicleNow = typeof vehicle.canRequestVehicle === "boolean" ? vehicle.canRequestVehicle : Number(vehicle.statusId) !== 4 && Number(vehicle.statusId) !== 6;

        const isRequestBlockedByPayment = Boolean(paymentStatus?.config?.isEnabled) && Boolean(paymentStatus?.config?.requirePaymentBeforeRequest) && Boolean(paymentStatus?.requiresPayment) && !Boolean(paymentStatus?.isPaid || paymentStatus?.paymentStatus === "paid");

        if (isRequestBlockedByPayment) {
            setError("Payment is required before request. Please complete payment first.");
            return;
        }

        if (!canRequestVehicleNow || requesting) return;

        try {
            setRequesting(true);
            setError(null);

            const res = await ValetApi.customerRequestVehicle(ticketNum, customerToken);
            setVehicle(res.data.customer);
        } catch (err) {
            const msg = Array.isArray(err) ? err[0] : "Failed to request vehicle. Please try again.";
            setError(msg);
        } finally {
            setRequesting(false);
        }
    };

    if (loading) return <div className="cvi-status">Loading...</div>;
    if (error) return <div className="cvi-error">{error}</div>;
    if (!vehicle) return <div className="cvi-status">No vehicle found.</div>;

    // plain derived values (no useMemo)
    const canRequestVehicle = typeof vehicle.canRequestVehicle === "boolean" ? vehicle.canRequestVehicle : Number(vehicle.statusId) !== 4 && Number(vehicle.statusId) !== 6;

    const isRequestBlockedByPayment = Boolean(paymentStatus?.config?.isEnabled) && Boolean(paymentStatus?.config?.requirePaymentBeforeRequest) && Boolean(paymentStatus?.requiresPayment) && !Boolean(paymentStatus?.isPaid || paymentStatus?.paymentStatus === "paid");

    const requestButtonText = vehicle.buttonText || (Number(vehicle.statusId) === 4 ? "Vehicle Requested" : "Request My Vehicle");

    // Pickup modal helpers
    const showPickupVerification = [4, 5].includes(Number(vehicle.statusId));
    const customerQrPayload = `PP:TICKET:${vehicle.ticketNum}`;

    return (
        <div className="customer-vehicle-info">
            <h2>Your Vehicle Details</h2>
            <ul>
                <li>
                    <strong>Ticket #:</strong> {vehicle.ticketNum}
                </li>
                <li>
                    <strong>Status:</strong> {vehicle.statusName}
                </li>
                <li>
                    <strong>Mobile:</strong> {formatPhone(vehicle.mobile)}
                </li>
                <li>
                    <strong>Color:</strong> {vehicle.color}
                </li>
                <li>
                    <strong>Make:</strong> {vehicle.make}
                </li>
                <li>
                    <strong>Location:</strong> {vehicle.locationName}
                </li>
                <li>
                    <strong>Podium:</strong> {vehicle.podiumName}
                </li>
            </ul>
            <div className="cvi-terms-section">
                <button
                    type="button"
                    onClick={() => setShowTerms((v) => !v)}
                    className="cvi-terms-toggle">
                    {showTerms ? "Hide Terms & Conditions" : "View Terms & Conditions"}
                </button>

                {showTerms && <div className="cvi-terms-text">{VALET_LIABILITY_TERMS}</div>}
            </div>
            <CustomerPaymentPanel
                ticketNum={ticketNum}
                customerToken={customerToken}
                vehicleStatusId={vehicle.statusId}
                onStatusUpdate={setPaymentStatus}
            />
            <button
                onClick={handleRequestVehicle}
                disabled={!canRequestVehicle || requesting || isRequestBlockedByPayment}
                className={`cvi-request-btn ${!canRequestVehicle || isRequestBlockedByPayment ? "is-requested is-disabled" : "is-default"}`}>
                {requesting ? "Submitting..." : requestButtonText}
            </button>
            {isRequestBlockedByPayment ? <div className="cvi-payment-warning">Payment is required before request.</div> : null}

            {/** START =>  PICKUP QR MODAL */}
            {showPickupVerification ? (
                <div
                    style={{
                        marginTop: "16px",
                        border: "1px solid #cdd7e1",
                        borderRadius: "10px",
                        padding: "12px",
                        background: "#f7fbff",
                        textAlign: "center",
                    }}>
                    <h4 style={{ marginBottom: "8px" }}>Pickup Verification</h4>
                    <div style={{ fontSize: "18px", fontWeight: 700, marginBottom: "8px" }}>Ticket #{vehicle.ticketNum}</div>
                    <QRCodeSVG
                        value={customerQrPayload}
                        size={170}
                        includeMargin
                    />
                    <div style={{ marginTop: "8px", fontSize: "12px", color: "#4f5d75" }}>Show this QR and ticket number to valet for checkout.</div>
                </div>
            ) : null}
            {/* END =>  PICKUP QR MODAL */}
            <div className="cvi-survey-section">
                <Link
                    to={canOpenSurvey && surveyPath && !surveyAlreadySubmitted ? surveyPath : "#"}
                    onClick={(e) => {
                        if (!canOpenSurvey || !surveyPath || surveyAlreadySubmitted) e.preventDefault();
                    }}
                    aria-disabled={!canOpenSurvey || !surveyPath || surveyAlreadySubmitted}
                    className={`cvi-survey-btn ${canOpenSurvey && surveyPath && !surveyAlreadySubmitted ? "is-active" : "is-disabled"}`}>
                    {surveyAlreadySubmitted ? "Survey Completed" : "Complete Survey"}
                </Link>
                <p className="cvi-survey-help">{surveyAlreadySubmitted ? "Thank you. Your survey has already been submitted for this visit." : "Survey unlocks after checkout and can be submitted once per visit."}</p>
            </div>
        </div>
    );
};

export default CustomerVehicleInfo;
